import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProjectList } from '../lib/projectList';
import { useAuth } from '../lib/auth';
import { useCustomer } from '../lib/customer';
import { useCurrency } from '../lib/currency';
import { supabase } from '../lib/supabase';
import { getTrackingSessionId, trackEvent } from '../lib/tracking';
import { theme } from '../styles/theme';
import { thumbUrl } from '../lib/imageUrl';

const WHATSAPP_PHONE = '525549332582';

type Mode = 'self' | 'to_client';

export function ProjectListPanel() {
  const { items, count, setQty, setNotes, remove, clear, open, setOpen } = useProjectList();
  const { session, user } = useAuth();
  const { customer } = useCustomer();
  const { fmtUsd, fmtMxn, usdMxn } = useCurrency();

  const tierDiscount = customer?.tier_discount_pct ?? 0;
  const canQuote = !!session && !!customer && tierDiscount > 0;

  // Helper: precio neto USD de un item con tier
  const itemCostUsd = (it: typeof items[0]): number => {
    if (it.cost_usd != null) return Number(it.cost_usd);
    if (it.msrp_usd != null) return Number(it.msrp_usd) * (1 - tierDiscount / 100);
    if (it.msrp_mxn != null && usdMxn) return Number(it.msrp_mxn) / usdMxn;
    return 0;
  };
  // === Modo de cotización + margen ===
  const [mode, setMode] = useState<Mode>('self');
  const [marginPct, setMarginPct] = useState(35); // % default sobre tu costo

  // === Totales según modo ===
  const subtotalUsd = items.reduce((sum, it) => {
    const unit = mode === 'self'
      ? itemCostUsd(it)
      : itemCostUsd(it) * (1 + marginPct / 100);
    return sum + unit * it.qty;
  }, 0);
  const taxUsd = subtotalUsd * 0.16;
  const totalUsd = subtotalUsd + taxUsd;
  const totalMxn = usdMxn ? totalUsd * usdMxn : 0;

  // Margen total $ ganados (solo en modo to_client)
  const marginUsd = mode === 'to_client'
    ? items.reduce((sum, it) => sum + itemCostUsd(it) * (marginPct / 100) * it.qty, 0)
    : 0;

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState<{ quote_id: string; quote_number: string; total: number; mode: Mode } | null>(null);

  const createQuote = async (selectedMode: Mode) => {
    setSubmitting(true);
    setSubmitError(null);
    setSubmitOk(null);
    try {
      const payload = items.map(it => ({
        product_id: it.product_id,
        sku: it.sku,
        name: it.name,
        brand_code: it.brand_code,
        qty: it.qty,
        unit: it.unit ?? 'pieza',
        notes: it.notes ?? null,
      }));
      const { data, error } = await supabase.rpc('create_customer_quote', {
        p_items: payload,
        p_project_title: `Mi proyecto · ${count} producto${count === 1 ? '' : 's'}`,
        p_notes: selectedMode === 'self'
          ? 'Cotización para mi compra a NULED (precio mayoreo)'
          : `Cotización al cliente final con margen sugerido ${marginPct}% sobre costo`,
        p_mode: selectedMode,
        p_margin_pct: selectedMode === 'to_client' ? marginPct : 0,
      });
      if (error) throw error;
      const row = (Array.isArray(data) ? data[0] : data) as { quote_id: string; quote_number: string; total: number } | undefined;
      if (!row) throw new Error('No se pudo crear la cotización');
      setSubmitOk({ quote_id: row.quote_id, quote_number: row.quote_number, total: Number(row.total), mode: selectedMode });
      void trackEvent('quote_created', {
        quote_id: row.quote_id,
        quote_number: row.quote_number,
        kind: selectedMode,
        currency: 'USD',
      }, usdMxn ? Number(row.total) * usdMxn : undefined, {
        customerId: customer?.id ?? null,
        userId: user?.id ?? null,
        dedupeKey: `quote_created:${row.quote_id}`,
      });
      clear();
    } catch (e: any) {
      setSubmitError(e.message ?? 'Error al crear cotización');
    } finally {
      setSubmitting(false);
    }
  };

  const sendToAgent = () => {
    const lines = items.map(it => {
      const cost = itemCostUsd(it) * it.qty;
      const priceLine = session && cost > 0
        ? `$${cost.toLocaleString('en-US', { maximumFractionDigits: 2 })} USD`
        : 'cotizar';
      const noteLine = it.notes ? `   _${it.notes}_` : '';
      return `• ${it.qty}x ${it.sku} — ${it.name} (${priceLine})${noteLine ? `\n${noteLine}` : ''}`;
    }).join('\n');

    const greeting = user?.email
      ? `Hola NULED, soy ${user.email}. Mi proyecto:`
      : 'Hola NULED, este es mi proyecto:';

    const total = session && subtotalUsd > 0
      ? `\n\nSubtotal estimado: $${subtotalUsd.toLocaleString('en-US', { maximumFractionDigits: 2 })} USD`
      : '';

    const leadRef = getTrackingSessionId();
    const msg = `${greeting}\n\n${lines}${total}\n\nQuiero una cotización formal y confirmar disponibilidad.\n\n_Referencia web: ${leadRef}_`;
    void trackEvent('click_whatsapp', {
      source: 'project_list',
      item_count: items.length,
    }, undefined, {
      customerId: customer?.id ?? null,
      userId: user?.id ?? null,
    });
    window.open(`https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <>
      {/* Floating trigger */}
      {count > 0 && !open && (
        <button
          onClick={() => setOpen(true)}
          aria-label={`Mi proyecto · ${count} productos`}
          style={{
            position: 'fixed',
            right: 24,
            bottom: 96,
            zIndex: 100,
            background: theme.brand,
            color: theme.textOnBrand,
            border: 'none',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            cursor: 'pointer',
            boxShadow: '0 6px 20px rgba(45,72,79,0.25)',
            fontFamily: theme.font.sans,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          <ListIcon />
          Mi proyecto
          <span style={{
            background: theme.accent,
            color: theme.brand,
            borderRadius: 999,
            padding: '2px 10px',
            fontSize: 12,
            fontWeight: 700,
          }}>
            {count}
          </span>
        </button>
      )}

      {/* Side panel */}
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 99,
            }}
          />
          <aside style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: 'min(440px, 100vw)',
            background: theme.bg,
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '-8px 0 24px rgba(0,0,0,0.15)',
            fontFamily: theme.font.sans,
            animation: 'fade-in 0.2s ease-out',
          }}>
            {/* Header */}
            <div style={{
              background: theme.brand,
              color: theme.textOnBrand,
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{
                  fontSize: 11,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                  color: theme.accent,
                  fontWeight: 700,
                }}>
                  Lista de proyecto
                </div>
                <div style={{
                  fontFamily: theme.font.head,
                  fontSize: 22,
                  fontWeight: 600,
                  marginTop: 2,
                }}>
                  Mi proyecto · {count} {count === 1 ? 'producto' : 'productos'}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Cerrar"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: theme.textOnBrand,
                  cursor: 'pointer',
                  padding: 8,
                  fontSize: 24,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            {/* Items */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
              {items.length === 0 && !submitOk ? (
                <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 14 }}>
                  Tu lista está vacía.
                  <br />
                  Agrega productos desde el catálogo.
                </div>
              ) : (
                items.map(it => (
                  <div key={it.sku} style={{
                    padding: '14px 0',
                    borderBottom: `1px solid ${theme.border}`,
                    display: 'grid',
                    gridTemplateColumns: '60px 1fr',
                    gap: 12,
                  }}>
                    <div style={{
                      width: 60,
                      height: 60,
                      background: theme.bg,
                      border: `1px solid ${theme.border}`,
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      {it.image_url && (
                        <img
                          src={thumbUrl(it.image_url, 160)}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          style={{
                            position: 'absolute', top: 0, left: 0,
                            width: '100%', height: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      )}
                    </div>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 10, color: theme.textMuted, fontFamily: theme.font.mono, textTransform: 'uppercase' }}>
                            {it.sku}
                          </div>
                          <div style={{ fontSize: 13, color: theme.brand, fontWeight: 500, lineHeight: 1.3, marginTop: 2 }}>
                            {it.name}
                          </div>
                        </div>
                        <button
                          onClick={() => remove(it.sku)}
                          aria-label="Quitar"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: theme.textMuted,
                            cursor: 'pointer',
                            padding: 4,
                            fontSize: 16,
                          }}
                        >
                          ×
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${theme.border}` }}>
                          <button
                            onClick={() => setQty(it.sku, it.qty - 1)}
                            disabled={it.qty <= 1}
                            style={qtyBtn}
                          >
                            −
                          </button>
                          <input
                            type="number"
                            value={it.qty}
                            aria-label={`Cantidad de ${it.name}`}
                            min={1}
                            onChange={e => setQty(it.sku, parseInt(e.target.value) || 1)}
                            style={{
                              width: 44,
                              padding: '4px 0',
                              border: 'none',
                              borderLeft: `1px solid ${theme.border}`,
                              borderRight: `1px solid ${theme.border}`,
                              textAlign: 'center',
                              fontSize: 13,
                              outline: 'none',
                              fontFamily: theme.font.sans,
                              color: theme.brand,
                            }}
                          />
                          <button onClick={() => setQty(it.sku, it.qty + 1)} style={qtyBtn}>+</button>
                        </div>
                        <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700, color: theme.brand }}>
                          {session ? fmtUsd(itemCostUsd(it) * it.qty) : 'Por cotizar'}
                        </span>
                      </div>
                      <input
                        placeholder="Nota (opcional, ej. 'lobby')"
                        value={it.notes ?? ''}
                        onChange={e => setNotes(it.sku, e.target.value)}
                        style={{
                          width: '100%',
                          marginTop: 8,
                          padding: '6px 8px',
                          border: `1px solid ${theme.border}`,
                          fontSize: 12,
                          fontFamily: theme.font.sans,
                          color: theme.brand,
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            {submitOk ? (
              <div style={{ borderTop: `1px solid ${theme.border}`, padding: 24, background: theme.bgFooter }}>
                <div style={{
                  background: theme.accent,
                  color: theme.brand,
                  padding: '16px 18px',
                  marginBottom: 16,
                }}>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                    ✓ Cotización creada · {submitOk.quote_number}
                  </div>
                  <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                    {submitOk.mode === 'self'
                      ? <>Cotización con <strong>tu precio mayoreo</strong>.</>
                      : <>Cotización a cliente final con margen sugerido <strong>{marginPct}%</strong>.</>}
                    <br />
                    Total: {fmtUsd(submitOk.total)} ({usdMxn ? fmtMxn(submitOk.total * usdMxn) : ''})
                    <br />
                    Nuestro agente confirma disponibilidad y envía instrucciones de pago.
                  </div>
                </div>
                {/* Descargar PDF */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                  <a
                    href={`/cotizacion/${submitOk.quote_id}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      background: theme.accent,
                      color: theme.brand,
                      textAlign: 'center',
                      padding: '12px 10px',
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      fontFamily: theme.font.sans,
                      textDecoration: 'none',
                      boxSizing: 'border-box',
                    }}
                  >
                    📥 PDF Cliente
                  </a>
                  <a
                    href={`/cotizacion/${submitOk.quote_id}/pdf?modo=interno`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      background: theme.bg,
                      color: theme.brand,
                      border: `1px solid ${theme.border}`,
                      textAlign: 'center',
                      padding: '12px 10px',
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      fontFamily: theme.font.sans,
                      textDecoration: 'none',
                      boxSizing: 'border-box',
                    }}
                  >
                    📋 PDF Interno
                  </a>
                </div>
                <Link
                  to="/portal/cotizaciones"
                  onClick={() => { setOpen(false); setSubmitOk(null); }}
                  style={{
                    display: 'block',
                    width: '100%',
                    background: theme.brand,
                    color: theme.textOnBrand,
                    textAlign: 'center',
                    padding: '14px 16px',
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    fontFamily: theme.font.sans,
                    textDecoration: 'none',
                    boxSizing: 'border-box',
                  }}
                >
                  Ver mi cotización
                </Link>
              </div>
            ) : items.length > 0 && (
              <div style={{ borderTop: `1px solid ${theme.border}`, padding: 20, background: theme.bgFooter }}>
                {canQuote ? (
                  <>
                    {/* Selector de modo */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 0,
                      marginBottom: 16,
                      border: `1px solid ${theme.border}`,
                    }}>
                      <ModeBtn active={mode === 'self'} onClick={() => setMode('self')}>
                        💰 Mi compra<br />
                        <small>precio mayoreo</small>
                      </ModeBtn>
                      <ModeBtn active={mode === 'to_client'} onClick={() => setMode('to_client')}>
                        🏷 Cotizar a cliente<br />
                        <small>precio público</small>
                      </ModeBtn>
                    </div>

                    {/* Margen ajustable cuando es a cliente */}
                    {mode === 'to_client' && (
                      <div style={{
                        padding: 12,
                        background: theme.bg,
                        border: `1px solid ${theme.border}`,
                        marginBottom: 12,
                      }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          marginBottom: 8,
                        }}>
                          <span style={{
                            fontSize: 10, color: theme.textMuted, fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: 0.5,
                          }}>
                            Margen sugerido
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 700, color: theme.brand }}>
                            {marginPct}%
                          </span>
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          step={1}
                          value={marginPct}
                          onChange={e => setMarginPct(Number(e.target.value))}
                          style={{
                            width: '100%',
                            accentColor: theme.accent,
                          }}
                        />
                        <div style={{
                          display: 'flex', justifyContent: 'space-between',
                          fontSize: 10, color: theme.textMuted, marginTop: 2,
                        }}>
                          <span>0%</span>
                          <span>50%</span>
                          <span>100%</span>
                        </div>
                        <div style={{
                          marginTop: 8,
                          fontSize: 11,
                          color: theme.accentDark,
                          fontWeight: 600,
                        }}>
                          Ganarías {fmtUsd(marginUsd)}
                          {usdMxn ? ` (${fmtMxn(marginUsd * usdMxn)})` : ''}
                        </div>
                      </div>
                    )}

                    {/* Totales */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                        Subtotal · {mode === 'self' ? 'tu costo' : 'precio cliente'}
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 600, color: theme.brand }}>
                        {fmtUsd(subtotalUsd)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                      <span style={{ fontSize: 11, color: theme.textMuted }}>IVA 16%</span>
                      <span style={{ fontSize: 12, color: theme.textMuted }}>{fmtUsd(taxUsd)}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: 12,
                      paddingTop: 8,
                      borderTop: `1px solid ${theme.border}`,
                    }}>
                      <span style={{ fontSize: 12, color: theme.brand, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Total
                      </span>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 600, color: theme.brand }}>
                          {fmtUsd(totalUsd)}
                        </div>
                        <div style={{ fontSize: 11, color: theme.textMuted }}>
                          ≈ {fmtMxn(totalMxn)}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => createQuote(mode)}
                      disabled={submitting || subtotalUsd <= 0}
                      style={{
                        width: '100%',
                        background: theme.brand,
                        color: theme.textOnBrand,
                        border: 'none',
                        padding: '14px 16px',
                        fontSize: 13,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        cursor: (submitting || subtotalUsd <= 0) ? 'not-allowed' : 'pointer',
                        fontFamily: theme.font.sans,
                        opacity: (submitting || subtotalUsd <= 0) ? 0.6 : 1,
                        marginBottom: 8,
                      }}
                    >
                      {submitting
                        ? 'Creando cotización…'
                        : mode === 'self'
                          ? 'Crear cotización'
                          : 'Cotizar a cliente (PVP + margen)'}
                    </button>
                  </>
                ) : session ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
                    <span style={{ fontSize: 11, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                      Subtotal estimado (PVP)
                    </span>
                    <span style={{ fontFamily: theme.font.head, fontSize: 22, fontWeight: 600, color: theme.brand }}>
                      {fmtUsd(subtotalUsd)}
                    </span>
                  </div>
                ) : (
                  <div style={{
                    fontSize: 12,
                    color: theme.textMuted,
                    marginBottom: 12,
                    fontStyle: 'italic',
                  }}>
                    Solicita tu cotización por WhatsApp sin crear una cuenta.
                  </div>
                )}

                <button
                  onClick={sendToAgent}
                  style={{
                    width: '100%',
                    background: canQuote ? 'transparent' : theme.accent,
                    color: theme.brand,
                    border: canQuote ? `1px solid ${theme.brand}` : 'none',
                    padding: '12px 16px',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    cursor: 'pointer',
                    fontFamily: theme.font.sans,
                  }}
                >
                  {canQuote ? 'O envíalo por WhatsApp' : 'Enviar al agente de ventas (WhatsApp)'}
                </button>

                {submitError && (
                  <div style={{
                    marginTop: 10,
                    padding: 10,
                    background: '#FEE2E2',
                    color: '#991B1B',
                    fontSize: 12,
                    border: '1px solid #FCA5A5',
                  }}>
                    {submitError}
                  </div>
                )}

                <button
                  onClick={clear}
                  style={{
                    width: '100%',
                    marginTop: 8,
                    background: 'transparent',
                    color: theme.textMuted,
                    border: 'none',
                    padding: '6px',
                    fontSize: 12,
                    cursor: 'pointer',
                    fontFamily: theme.font.sans,
                    textDecoration: 'underline',
                  }}
                >
                  Vaciar lista
                </button>
              </div>
            )}
          </aside>
        </>
      )}
    </>
  );
}

function ModeBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : theme.bg,
      color: active ? theme.textOnBrand : theme.brand,
      border: 'none',
      padding: '10px 8px',
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      cursor: 'pointer',
      fontFamily: theme.font.sans,
      lineHeight: 1.4,
    }}>
      {children}
    </button>
  );
}

const qtyBtn: React.CSSProperties = {
  background: theme.bg,
  border: 'none',
  width: 28,
  height: 28,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 700,
  color: theme.brand,
};

function ListIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <circle cx="3.5" cy="6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="3.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="3.5" cy="18" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}
